import { useState } from 'react';
import { Box } from '@mui/system';
import { WidgetContextProps, WidgetOption, WidgetSelector } from './WidgetSelector';
import { ForcedRankingWidget } from './ForcedRankingWidget';
import { InlineRankingWidget } from './InlineRankingWidget';
import { SemanticRankingWidget } from './SemanticRankingWidget';
import { ClassifierRankingWidget } from './ClassifierRankingWidget';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetRanking = Models.SetsPlayground.SetRanking;
type RankingType = SetRanking['type'];

interface Props {
    index: number;
    set: SetDefinition;
    widgets?: WidgetOption<SetRanking>[];
    widgetProps?: WidgetContextProps;
    onChangeRanking: (index: number, ranking: SetRanking) => void;
}

const DEFAULT_RANKING_WIDGETS: WidgetOption<SetRanking>[] = [
    ForcedRankingWidget as WidgetOption<SetRanking>,
    InlineRankingWidget as WidgetOption<SetRanking>,
    SemanticRankingWidget as WidgetOption<SetRanking>,
    ClassifierRankingWidget as WidgetOption<SetRanking>,
];

export const RankingWidgetContainer = ({
    index, set, widgets, widgetProps, onChangeRanking,
}: Props): JSX.Element => {
    const { ranking } = set;
    const selected = ranking.type;
    const [isRankingSelected, setIsRankingSelected] = useState(false);

    const rankingWidgets = widgets ?? DEFAULT_RANKING_WIDGETS;

    const noneRanking: boolean = ranking.type === 'forced'
        && ranking.rankingStrategies.length === 1
        && ranking.rankingStrategies[0].name === ''
        && !isRankingSelected;

    const handleWidgetChange = (value: string): void => {
        if (value === 'none') {
            setIsRankingSelected(false);
            onChangeRanking(index, {
                type: 'forced',
                rankingStrategies: [{ name: '', sortingDirection: 'descending' }],
            });
            return;
        }

        setIsRankingSelected(true);
        const nextType = value as RankingType;

        if (nextType !== ranking.type || noneRanking) {
            const widget = rankingWidgets.find((w) => w.value === nextType);

            if (widget?.getDefaultValue) {
                const defaultValue = widget.getDefaultValue();

                onChangeRanking(index, defaultValue);
            }
        }
    };

    const handleRankingChange = (newRanking: SetRanking): void => {
        onChangeRanking(index, newRanking);
    };

    return (
        <Box>
            <WidgetSelector
                showNoneOption
                label="Ranking Type"
                selected={noneRanking ? 'none' : selected}
                widgetProps={widgetProps}
                widgets={rankingWidgets}
                widgetValue={ranking}
                onWidgetChange={handleWidgetChange}
                onWidgetValueChange={handleRankingChange}
            />
        </Box>
    );
};
